import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UserDashboardLayout from "../../layouts/UserDashboardLayout";
import { 
  FiTarget, FiTrendingUp, FiAward, FiCalendar, FiFeather, 
  FiCheck, FiInfo, FiBell, FiDroplet, FiHeart, FiWind, 
  FiStar, FiShield, FiClock, FiVideo 
} from "react-icons/fi";
import Topbar from "../../components/userDashboard/Topbar";
import BrandLoader from "../../components/BrandLoader";
import { useCheckIn } from "../../context/CheckInContext";
import { practiceVideoAPI, dietPlanAPI, userAPI, goalsAPI } from "../../services/api";

// Category and styling helpers
const getCategoryStyles = (category, title) => {
  const cat = (category || "").toLowerCase();
  const t = (title || "").toLowerCase();

  if (cat === "practice" || t.includes("session") || t.includes("video")) {
    return {
      accent: "#F29A0C", accent2: "#E97818", bg: "rgba(255,244,232,0.85)", border: "rgba(242,154,12,0.22)", icon: FiCalendar
    };
  }
  if (cat === "mindfulness" || t.includes("mindful")) {
    return {
      accent: "#6B8A5A", accent2: "#8BA47A", bg: "rgba(245,250,242,0.85)", border: "rgba(107,138,90,0.22)", icon: FiClock
    };
  }
  if (cat === "nutrition" || t.includes("diet") || t.includes("meal")) {
    return {
      accent: "#185FA5", accent2: "#378ADD", bg: "rgba(230,241,251,0.8)", border: "rgba(24,95,165,0.2)", icon: FiDroplet
    };
  }
  if (t.includes("score")) {
    return {
      accent: "#E97818", accent2: "#C8600F", bg: "rgba(255,244,232,0.8)", border: "rgba(233,120,24,0.2)", icon: FiHeart
    };
  }
  if (cat === "strength") {
    return {
      accent: "#E24B4A", accent2: "#F29A0C", bg: "rgba(255,240,240,0.8)", border: "rgba(226,75,74,0.2)", icon: FiTarget
    };
  }
  // Default (wellness / other)
  return {
    accent: "#534AB7", accent2: "#7F77DD", bg: "rgba(238,237,254,0.8)", border: "rgba(83,74,183,0.2)", icon: FiWind
  };
};

// ─── DESIGN TOKENS ──────────────────────────────────────────────────────────
const T = {
  orange: "#F29A0C", orange2: "#E97818",
  olive: "#6B8A5A",  olive2: "#8BA47A",
  dark: "#1F1F1F",   mid: "#555",
  muted: "#888",     light: "#AAA",
  border: "rgba(239,229,216,0.8)",
};

const glass = {
  background: "rgba(255,255,255,0.85)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

const CATEGORIES = ["All", "Practice", "Wellness", "Mindfulness", "Nutrition", "Strength"];

// ─── GOAL RING ────────────────────────────────────────────────────────────────
function GoalRing({ pct, accent, accent2, size = 110, strokeWidth = 8, delay = 0 }) {
  const r = (size - strokeWidth * 2) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const id = `gr-${Math.random().toString(36).slice(2)}`;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} stroke="rgba(239,229,216,0.6)" strokeWidth={strokeWidth} fill="none" />
        <motion.circle cx={size/2} cy={size/2} r={r} stroke={`url(#${id})`} strokeWidth={strokeWidth} fill="none"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.4, delay, ease: "easeOut" }} />
        <defs>
          <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={accent} /><stop offset="100%" stopColor={accent2} />
          </linearGradient>
        </defs>
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: size * 0.22, fontWeight: 900, color: T.dark, letterSpacing: -1, lineHeight: 1 }}>
          {pct}<span style={{ fontSize: size * 0.13 }}>%</span>
        </p>
        <p style={{ fontSize: 9, color: T.muted, fontWeight: 700, marginTop: 2 }}>DONE</p>
      </div>
    </div>
  );
}

// ─── PROGRESS BAR ─────────────────────────────────────────────────────────────
function ProgressBar({ pct, accent, accent2, delay = 0 }) {
  return (
    <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(239,229,216,0.6)" }}>
      <motion.div className="h-full rounded-full"
        style={{ background: `linear-gradient(90deg,${accent},${accent2})` }}
        initial={{ width: 0 }} animate={{ width: `${pct}%` }}
        transition={{ duration: 1.1, delay, ease: "easeOut" }} />
    </div>
  );
}

// ─── MINI BAR CHART ───────────────────────────────────────────────────────────
function MiniChart({ history, accent, accent2, currentWeekIndex = 0 }) {
  const max = Math.max(...history, 1);
  return (
    <div className="flex items-end gap-2 h-14 pt-2">
      {history.map((v, i) => {
        const isCurrentWeek = i === currentWeekIndex;
        const isFutureWeek = i > currentWeekIndex;
        const barHeightPx = isFutureWeek ? 6 : Math.max(6, Math.round((v / max) * 36));
        return (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
            <span className="text-[9px] font-bold" style={{ color: isCurrentWeek ? accent : isFutureWeek ? "#CCC" : T.muted }}>
              {isFutureWeek ? "-" : `${v}`}
            </span>
            <motion.div className="w-full rounded-t-md"
              initial={{ height: 0 }}
              animate={{ height: `${barHeightPx}px` }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: "easeOut" }}
              style={{
                background: isCurrentWeek 
                  ? `linear-gradient(180deg,${accent},${accent2})` 
                  : isFutureWeek 
                  ? "rgba(220,220,220,0.35)" 
                  : `${accent}40`,
                boxShadow: isCurrentWeek ? `0 4px 12px ${accent}45` : "none",
                minHeight: 6,
              }} />
            <span style={{ 
              fontSize: 9, 
              fontWeight: isCurrentWeek ? 800 : 600, 
              color: isCurrentWeek ? accent : isFutureWeek ? "#BBB" : T.muted 
            }}>
              W{i + 1}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── GOAL CARD COMPONENT ─────────────────────────────────────────────────────
function GoalCard({ goal, delay, currentWeekIndex = 0 }) {
  const Icon = goal.icon;
  const urgency = goal.pct < 50
    ? { label: "Needs Attention", color: "#E24B4A", bg: "rgba(226,75,74,0.1)" }
    : goal.pct < 75
    ? { label: "In Progress",     color: T.orange,  bg: "rgba(242,154,12,0.1)" }
    : { label: "On Track",        color: T.olive,   bg: "rgba(107,138,90,0.1)" };

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }} transition={{ delay, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8, boxShadow: `0 28px 70px rgba(0,0,0,0.1)` }}
      className="relative rounded-[24px] p-5 overflow-hidden cursor-default flex flex-col justify-between"
      style={{ background: goal.bg, border: `1px solid ${goal.border}`,
        boxShadow: "0 16px 50px rgba(0,0,0,0.05)", transition: "box-shadow 0.32s" }}>

      <div>
        {/* Header */}
        <div className="flex items-start justify-between mb-4 relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-[14px] flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg,${goal.accent},${goal.accent2})`, boxShadow: `0 8px 20px ${goal.accent}45` }}>
              <Icon size={20} style={{ color: "#fff" }} />
            </div>
            <div>
              <p className="font-black text-[15px] text-[#1F1F1F]"
                style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.3px" }}>{goal.title}</p>
              <p className="text-[11px] text-zinc-500 font-medium">Deadline: {goal.deadline}</p>
            </div>
          </div>
          <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold"
            style={{ background: urgency.bg, color: urgency.color }}>
            {urgency.label}
          </span>
        </div>

        {/* Ring + Progress Values */}
        <div className="flex items-center gap-4 mb-4">
          <GoalRing pct={goal.pct} accent={goal.accent} accent2={goal.accent2} delay={delay + 0.1} />
          <div className="flex-1">
            <div className="flex justify-between items-baseline mb-2">
              <div>
                <p className="font-black text-[#1F1F1F] leading-none"
                  style={{ fontFamily: "'Poppins',sans-serif", fontSize: 26, letterSpacing: "-1px" }}>
                  {goal.current.toLocaleString()}
                </p>
                <p className="text-[11px] mt-0.5 text-zinc-500 font-medium">{goal.unit}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] font-bold" style={{ color: goal.accent }}>Monthly Target</p>
                <p className="font-bold text-[17px] text-[#1F1F1F]"
                  style={{ fontFamily: "'Poppins',sans-serif" }}>{goal.target.toLocaleString()}</p>
              </div>
            </div>
            <ProgressBar pct={goal.pct} accent={goal.accent} accent2={goal.accent2} delay={delay + 0.2} />
          </div>
        </div>

        {/* 4-Week Activity Trend with Zero Overlap */}
        <div className="rounded-[16px] p-3 mb-4" style={{ background: "rgba(255,255,255,0.75)", border: "1px solid rgba(255,255,255,0.9)" }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-extrabold uppercase tracking-[0.8px] text-zinc-500">4-Week Activity Trend</p>
            <span className="text-[10px] font-extrabold text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
              Active: W{currentWeekIndex + 1}
            </span>
          </div>
          <MiniChart history={goal.history} accent={goal.accent} accent2={goal.accent2} currentWeekIndex={currentWeekIndex} />
        </div>
      </div>

      {/* Milestone Badge Pill using Vector Icons (NO EMOJIS) */}
      {goal.pct >= 100 ? (
        <div className="rounded-[14px] p-2.5 flex items-center justify-between gap-2"
          style={{ background: "linear-gradient(135deg, rgba(242,154,12,0.15), rgba(233,120,24,0.15))", border: "1.5px solid rgba(242,154,12,0.35)" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-sm">
              <FiAward size={13} />
            </div>
            <div>
              <p className="font-extrabold text-[11px] text-amber-950 leading-tight">Gold Badge Unlocked!</p>
              <p className="text-[9px] text-amber-800 font-medium">Achieved (+{goal.pointsReward || 150} pts)</p>
            </div>
          </div>
          <span className="text-[9px] font-extrabold px-2.5 py-0.5 rounded-full bg-amber-500 text-white shadow-sm flex items-center gap-1">
            <FiAward size={10} /> GOLD
          </span>
        </div>
      ) : goal.pct >= 75 ? (
        <div className="rounded-[14px] p-2.5 flex items-center justify-between gap-2"
          style={{ background: "linear-gradient(135deg, rgba(107,138,90,0.15), rgba(139,164,122,0.15))", border: "1.5px solid rgba(107,138,90,0.35)" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-sm">
              <FiStar size={12} />
            </div>
            <div>
              <p className="font-extrabold text-[11px] text-emerald-950 leading-tight">Silver Badge Progress</p>
              <p className="text-[9px] text-emerald-800 font-medium">Reach 100% to earn Gold Badge</p>
            </div>
          </div>
          <span className="text-[9px] font-extrabold px-2.5 py-0.5 rounded-full bg-emerald-600 text-white shadow-sm flex items-center gap-1">
            <FiStar size={10} /> SILVER
          </span>
        </div>
      ) : goal.pct >= 50 ? (
        <div className="rounded-[14px] p-2.5 flex items-center justify-between gap-2"
          style={{ background: "rgba(205,127,50,0.1)", border: "1.5px solid rgba(205,127,50,0.25)" }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-amber-700 text-white flex items-center justify-center shadow-sm">
              <FiShield size={12} />
            </div>
            <div>
              <p className="font-extrabold text-[11px] text-amber-950 leading-tight">Bronze Badge Progress</p>
              <p className="text-[9px] text-amber-800 font-medium">Keep going for Silver & Gold</p>
            </div>
          </div>
          <span className="text-[9px] font-extrabold px-2.5 py-0.5 rounded-full bg-amber-700 text-white shadow-sm flex items-center gap-1">
            <FiShield size={10} /> BRONZE
          </span>
        </div>
      ) : (
        <div className="rounded-[14px] p-2.5 flex items-start gap-2" style={{ background: "rgba(255,255,255,0.6)" }}>
          <FiInfo size={13} style={{ color: goal.accent, flexShrink: 0, marginTop: 1 }} />
          <p className="text-[10px] leading-[1.45]" style={{ color: T.mid }}>{goal.tip}</p>
        </div>
      )}
    </motion.div>
  );
}

// ─── HISTORY BAR CHART ────────────────────────────────────────────────────────
function HistoryChart({ goals, currentWeekIndex = 0 }) {
  const weeks = ["Wk 1", "Wk 2", "Wk 3", "Wk 4"];
  const weeklyAvg = weeks.map((_, wi) =>
    goals.length ? Math.round(goals.reduce((s, g) => s + (g.history[wi] || 0), 0) / goals.length) : 0
  );
  const maxV = Math.max(...weeklyAvg, 1);

  return (
    <div className="rounded-[28px] p-7" style={glass}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins',sans-serif", letterSpacing: "-0.4px" }}>
            Goal Progress History
          </h2>
          <p className="text-[12px] mt-0.5 text-zinc-500">Weekly activity average across monthly goals</p>
        </div>
        <span className="text-[11px] font-extrabold px-3 py-1 rounded-full bg-amber-500/10 text-amber-800 border border-amber-500/20">
          Current Active Week: Wk {currentWeekIndex + 1}
        </span>
      </div>
      <div className="flex items-end gap-4" style={{ height: 120 }}>
        {weeklyAvg.map((v, i) => {
          const isCurrentWeek = i === currentWeekIndex;
          const isFutureWeek = i > currentWeekIndex;
          const valToShow = isFutureWeek ? 0 : v;
          const h = Math.round((valToShow / maxV) * 100);
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <p className="text-[11px] font-bold" style={{ color: isCurrentWeek ? T.orange : isFutureWeek ? "#CCC" : T.muted }}>
                {valToShow}%
              </p>
              <motion.div className="w-full rounded-t-[8px]"
                initial={{ height: 0 }} animate={{ height: `${isFutureWeek ? 8 : h}%` }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }}
                style={{
                  background: isCurrentWeek 
                    ? `linear-gradient(180deg,${T.orange},${T.orange2})` 
                    : isFutureWeek 
                    ? "rgba(220,220,220,0.3)" 
                    : "rgba(242,154,12,0.25)",
                  boxShadow: isCurrentWeek ? `0 4px 16px rgba(242,154,12,0.42)` : "none",
                  minHeight: 8,
                }} />
              <p className="text-[11px] font-bold" style={{ color: isCurrentWeek ? T.orange : isFutureWeek ? "#CCC" : T.muted }}>
                {weeks[i]}{isCurrentWeek ? " (Active)" : ""}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── MAIN GOALS PAGE ──────────────────────────────────────────────────────────
export default function GoalsPage() {
  const { stats } = useCheckIn();
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeTab, setActiveTab]       = useState("active");
  const [apiGoals, setApiGoals]         = useState([]);

  const now = useMemo(() => new Date(), []);
  const currentMonthName = now.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysLeftInMonth = daysInMonth - now.getDate();
  const currentWeekIndex = useMemo(() => Math.min(3, Math.floor((now.getDate() - 1) / 7)), [now]);

  const fetchGoalsAndSync = async () => {
    try {
      setLoading(true);

      const isCurrentMonth = (dateInput) => {
        if (!dateInput) return true;
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return true;
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      };

      // 1. Fetch practice videos progress (Filter current month completed videos)
      const videoRes = await practiceVideoAPI.getProgress().catch(() => null);
      let completedVideosCount = 0;
      if (videoRes?.data) {
        const dataObj = videoRes.data.data ?? videoRes.data.Data;
        if (dataObj && typeof dataObj === 'object') {
          const entries = Object.entries(dataObj);
          completedVideosCount = entries.filter(([_, val]) => {
            if (typeof val === 'number') return val === 100;
            if (typeof val === 'object' && val !== null) {
              const is100 = (val.progress === 100 || val.pct === 100);
              const dateVal = val.updatedAt || val.completedAt || val.timestamp;
              return is100 && (dateVal ? isCurrentMonth(dateVal) : true);
            }
            return false;
          }).length;
        }
      }

      // 2. Fetch diet plan completed meals for current month
      const dietRes = await dietPlanAPI.getMyPlan().catch(() => null);
      let completedMealsCount = 0;
      if (dietRes?.data) {
        const rawDietData = dietRes.data.data ?? dietRes.data.Data ?? dietRes.data;
        const completedMeals = rawDietData?.completedLogs || rawDietData?.CompletedLogs || rawDietData?.completedMeals || rawDietData?.CompletedMeals;
        if (completedMeals && Array.isArray(completedMeals)) {
          const currentMonthMeals = completedMeals.filter(m => {
            const dVal = m.completedAt || m.CompletedAt || m.dateStr || m.DateStr;
            return dVal ? isCurrentMonth(dVal) : true;
          });
          completedMealsCount = currentMonthMeals.length;
        }
      }

      // 3. Fetch mindfulness stats
      const mindRes = await userAPI.getMindfulness().catch(() => null);
      let mindfulnessMinutes = 0;
      if (mindRes?.data) {
        mindfulnessMinutes = mindRes.data.totalMinutes ?? mindRes.data.TotalMinutes ?? mindRes.data.data?.totalMinutes ?? 0;
      }

      const wellnessScore = stats?.wellnessScore ?? 0;

      // Sync backend goals API
      const syncRes = await goalsAPI.syncGoals({
        mindfulnessMinutes,
        completedVideosCount,
        completedMealsCount,
        wellnessScore
      }).catch(() => null);

      if (syncRes?.data?.data || syncRes?.data?.Data) {
        const rawGoals = syncRes.data.data ?? syncRes.data.Data;
        setApiGoals(rawGoals);
      } else {
        const getRes = await goalsAPI.getGoals().catch(() => null);
        if (getRes?.data?.data || getRes?.data?.Data) {
          setApiGoals(getRes.data.data ?? getRes.data.Data);
        }
      }
    } catch (e) {
      console.warn("Error syncing goals with backend API:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoalsAndSync();
  }, [stats]);

  // Construct 5 Core Monthly Goals (Backend API + Client fallback)
  const processedGoals = useMemo(() => {
    const totalPractices = stats?.totalPractices ?? 0;
    const wellnessScore = stats?.wellnessScore ?? 0;

    // Fallback core templates
    const fallbackTemplates = [
      {
        title: "Monthly Sessions",
        category: "practice",
        target: 10,
        current: totalPractices,
        unit: "sessions",
        tip: "Log your attendance by checking in at live classes.",
        pointsReward: 150
      },
      {
        title: "Mindfulness Minutes",
        category: "mindfulness",
        target: 600,
        unit: "minutes",
        current: 0,
        tip: "Aggregated automatically from attended live sessions, watched practice videos, and mindfulness logs.",
        pointsReward: 200
      },
      {
        title: "Practice Videos Watched",
        category: "practice",
        target: 15,
        unit: "videos",
        current: 0,
        tip: "Complete videos in your practice library to track progress.",
        pointsReward: 120
      },
      {
        title: "Diet Plan Completed Meals",
        category: "nutrition",
        target: 80,
        unit: "meals",
        current: 0,
        tip: "Tick off your daily breakfast, lunch, and dinner to meet your goal.",
        pointsReward: 80
      },
      {
        title: "Wellness Score",
        category: "wellness",
        target: 100,
        current: wellnessScore,
        unit: "score",
        tip: "Your score is calculated based on daily check-in consistency.",
        pointsReward: 100
      }
    ];

    const sourceGoals = (apiGoals && apiGoals.length > 0) ? apiGoals : fallbackTemplates;

    // Deduplicate goals by title to guarantee exactly 1 card per title
    const uniqueGoalsMap = new Map();
    sourceGoals.forEach(g => {
      const titleKey = (g.title || g.Title || "Goal").toString().trim().toLowerCase();
      if (!uniqueGoalsMap.has(titleKey)) {
        uniqueGoalsMap.set(titleKey, g);
      }
    });

    return Array.from(uniqueGoalsMap.values()).map(g => {
      const category = g.category || g.Category || "wellness";
      const title = g.title || g.Title || "Goal";
      const target = g.target || g.Target || 10;
      const current = g.current || g.Current || 0;
      const unit = g.unit || g.Unit || "units";
      const tip = g.tip || g.Tip || "Stay consistent to reach your monthly goal.";
      const isCompleted = g.isCompleted || g.IsCompleted || current >= target;
      const pointsReward = g.pointsReward || g.PointsReward || 150;
      const historyCsv = g.historyCsv || g.HistoryCsv || `${Math.round(current * 0.4)},0,0,0`;

      const styles = getCategoryStyles(category, title);
      const pctVal = Math.min(100, Math.round((current / target) * 100));
      
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const deadlineStr = endOfMonth.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      
      const historyList = historyCsv.split(',').map(n => parseFloat(n) || 0);

      return {
        id: g.id || g.Id || title,
        title,
        category,
        target,
        current,
        unit,
        tip,
        pointsReward,
        pct: pctVal,
        deadline: deadlineStr,
        history: historyList.length === 4 ? historyList : [current, 0, 0, 0],
        isCompleted: isCompleted || pctVal >= 100,
        completedAt: g.completedAt || g.CompletedAt,
        ...styles
      };
    });
  }, [apiGoals, stats, now]);

  // Extract individual 5 core metrics for the Stat Strip Header
  const metricsSummary = useMemo(() => {
    const liveGoal = processedGoals.find(g => g.title.toLowerCase().includes("session")) || { current: stats?.totalPractices || 0, target: 10 };
    const videoGoal = processedGoals.find(g => g.title.toLowerCase().includes("video")) || { current: 0, target: 15 };
    const dietGoal = processedGoals.find(g => g.title.toLowerCase().includes("diet") || g.title.toLowerCase().includes("meal")) || { current: 0, target: 80 };
    const mindGoal = processedGoals.find(g => g.title.toLowerCase().includes("mindfulness")) || { current: 0, target: 600 };
    const wellnessGoal = processedGoals.find(g => g.title.toLowerCase().includes("score")) || { current: stats?.wellnessScore || 0, target: 100 };

    return [
      { id: "sessions", title: "Live Sessions", val: `${liveGoal.current} / ${liveGoal.target}`, label: "Sessions Count", icon: FiVideo, accent: "#F29A0C" },
      { id: "videos", title: "Practice Videos", val: `${videoGoal.current} / ${videoGoal.target}`, label: "Videos Watched", icon: FiCalendar, accent: "#534AB7" },
      { id: "diet", title: "Diet Meals", val: `${dietGoal.current} / ${dietGoal.target}`, label: "Meals Logged", icon: FiDroplet, accent: "#185FA5" },
      { id: "mindfulness", title: "Mindfulness", val: `${mindGoal.current} / ${mindGoal.target}`, label: "Minutes Logged", icon: FiClock, accent: "#6B8A5A" },
      { id: "wellness", title: "Wellness Score", val: `${wellnessGoal.current} / ${wellnessGoal.target}`, label: "Score Level", icon: FiHeart, accent: "#E97818" },
    ];
  }, [processedGoals, stats]);

  // Past Months Archived Goals (strictly from Database API)
  const previousMonthsArchive = useMemo(() => {
    if (!apiGoals || apiGoals.length === 0) return [];
    return apiGoals
      .filter(g => {
        const createdAt = g.createdAt || g.CreatedAt;
        if (!createdAt) return false;
        const d = new Date(createdAt);
        return d.getFullYear() < now.getFullYear() || (d.getFullYear() === now.getFullYear() && d.getMonth() < now.getMonth());
      })
      .map(g => {
        const category = g.category || g.Category || "wellness";
        const title = g.title || g.Title || "Goal";
        const target = g.target || g.Target || 10;
        const current = g.current || g.Current || 0;
        const unit = g.unit || g.Unit || "units";
        const pointsReward = g.pointsReward || g.PointsReward || 150;
        const createdAt = g.createdAt || g.CreatedAt;
        const monthName = createdAt ? new Date(createdAt).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : "Past Month";
        const styles = getCategoryStyles(category, title);
        const pctVal = Math.min(100, Math.round((current / target) * 100));

        return {
          id: g.id || g.Id || title,
          title,
          category,
          target,
          current,
          unit,
          pointsReward,
          pct: pctVal,
          monthName,
          completedAt: g.completedAt || g.CompletedAt || createdAt,
          ...styles
        };
      });
  }, [apiGoals, now]);

  const activeGoals = useMemo(() => {
    return processedGoals.filter(g => !g.isCompleted && g.pct < 100);
  }, [processedGoals]);

  const completedGoals = useMemo(() => {
    return processedGoals.filter(g => g.isCompleted || g.pct >= 100);
  }, [processedGoals]);

  const filteredGoals = useMemo(() => {
    const targetList = activeTab === "active" ? (activeGoals.length > 0 ? activeGoals : processedGoals) : completedGoals;
    return targetList.filter(g =>
      activeFilter === "All" || g.category.toLowerCase() === activeFilter.toLowerCase()
    );
  }, [activeGoals, completedGoals, processedGoals, activeTab, activeFilter]);

  const overallPct = useMemo(() => {
    if (processedGoals.length === 0) return 0;
    return Math.round(processedGoals.reduce((s, g) => s + g.pct, 0) / processedGoals.length);
  }, [processedGoals]);

  const completedGoalsCount = useMemo(() => {
    return completedGoals.length;
  }, [completedGoals]);

  const completedPct = useMemo(() => {
    if (processedGoals.length === 0) return 0;
    return Math.round((completedGoalsCount / processedGoals.length) * 100);
  }, [processedGoals, completedGoalsCount]);

  const onTrack = useMemo(() => {
    return processedGoals.filter(g => g.pct >= 50).length;
  }, [processedGoals]);

  const R1 = 66, C1 = 2 * Math.PI * R1, O1 = C1 - (overallPct / 100) * C1;
  const R2 = 48, C2 = 2 * Math.PI * R2, O2 = C2 - (completedPct / 100) * C2;

  if (loading) {
    return (
      <UserDashboardLayout>
        <Topbar />
        <BrandLoader 
          message="Loading Wellness Goals..." 
          subMessage="Preparing your health objectives and milestone progress" 
        />
      </UserDashboardLayout>
    );
  }

  return (
    <UserDashboardLayout>
      <div style={{ fontFamily: "'Inter',sans-serif" }}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

        {/* Ambient background glows */}
        <motion.div animate={{ y: [0,-22,0], x:[0,14,0] }} transition={{ duration:13, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[460px] h-[460px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.08),transparent 70%)", filter:"blur(80px)", top:-140, right:-90 }} />
        <motion.div animate={{ y:[0,16,0], x:[0,-10,0] }} transition={{ duration:16, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[380px] h-[380px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.07),transparent 70%)", filter:"blur(80px)", bottom:-80, left:-80 }} />

        <div className="relative z-10 w-full min-w-0">
          <Topbar/>

          {/* HERO BANNER */}
          <motion.div initial={{ opacity:0, y:24 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.7 }}
            className="relative overflow-hidden rounded-[36px] p-6 sm:p-8 md:p-10 mb-6" style={glass}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] rounded-[inherit]"
              style={{ backgroundImage:"radial-gradient(circle,#000 1px,transparent 0)", backgroundSize:"22px 22px" }} />

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_200px] xl:grid-cols-[1fr_220px] gap-6 lg:gap-8 items-center relative">
              <div>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <motion.div animate={{ boxShadow: ["0 0 0 0 rgba(242,154,12,0)", "0 0 0 8px rgba(242,154,12,0.14)", "0 0 0 0 rgba(242,154,12,0)"] }}
                    transition={{ duration: 2.8, repeat: Infinity }}
                    className="w-11 h-11 rounded-[16px] flex items-center justify-center"
                    style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 8px 22px rgba(242,154,12,0.42)` }}>
                    <FiTarget size={20} style={{ color:"#fff" }} />
                  </motion.div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[1.5px]" style={{ color:T.orange }}>{currentMonthName} Target Cycle</p>
                    <p className="text-[12px] text-zinc-500 font-medium">{daysLeftInMonth} Days remaining in this cycle · {processedGoals.length} Tracked Goals</p>
                  </div>
                  <div className="ml-auto sm:ml-2 flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-extrabold"
                    style={{ background:"rgba(107,138,90,0.12)", border:"1px solid rgba(107,138,90,0.25)", color:"#27500A" }}>
                    <FiTrendingUp size={12} /> Current Month Active
                  </div>
                </div>

                <h1 className="font-black mb-3 text-[26px] sm:text-[32px] lg:text-[34px]"
                  style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.5px", lineHeight:1.06, color:T.dark }}>
                  Monthly Goals &{" "}
                  <span style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
                    Milestone Badges.
                  </span>
                </h1>
                <p className="text-[14px] leading-[1.8] text-zinc-500" style={{ maxWidth:520 }}>
                  Track your 5 core monthly targets with live backend updates. Unlock Bronze, Silver & Gold vector badges as you achieve each milestone.
                </p>
              </div>

              {/* Concentric Progress Rings */}
              <div className="text-center flex-shrink-0 flex flex-col items-center justify-center">
                <div className="mx-auto" style={{ position:"relative", width:150, height:150 }}>
                  <svg viewBox="0 0 160 160" style={{ position:"absolute", inset:0, width:"100%", height:"100%", transform:"rotate(-90deg)" }}>
                    <circle cx="80" cy="80" r={R1} stroke="#F1E8DA" strokeWidth="11" fill="none" />
                    <motion.circle cx="80" cy="80" r={R1} stroke="url(#hg1)" strokeWidth="11" fill="none" strokeLinecap="round"
                      strokeDasharray={C1} initial={{ strokeDashoffset: C1 }} animate={{ strokeDashoffset: O1 }}
                      transition={{ duration:1.6, delay:0.3, ease:"easeOut" }} />
                    <circle cx="80" cy="80" r={R2} stroke="#ECF5E8" strokeWidth="8" fill="none" />
                    <motion.circle cx="80" cy="80" r={R2} stroke="url(#hg2)" strokeWidth="8" fill="none" strokeLinecap="round"
                      strokeDasharray={C2} initial={{ strokeDashoffset: C2 }} animate={{ strokeDashoffset: O2 }}
                      transition={{ duration:1.6, delay:0.5, ease:"easeOut" }} />
                    <defs>
                      <linearGradient id="hg1" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={T.orange}/><stop offset="100%" stopColor={T.orange2}/></linearGradient>
                      <linearGradient id="hg2" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={T.olive}/><stop offset="100%" stopColor={T.olive2}/></linearGradient>
                    </defs>
                  </svg>
                  <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                    <p className="font-black leading-none" style={{ fontFamily:"'Poppins',sans-serif", fontSize:36, letterSpacing:"-1.5px", color:T.dark }}>{overallPct}%</p>
                    <p className="text-[10px] font-medium text-zinc-500">Overall Progress</p>
                  </div>
                </div>
                <div className="mt-2 text-center w-full">
                  <p className="font-black text-[13px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                    {onTrack} / {processedGoals.length} On Track
                  </p>
                  <p className="text-[10px] text-zinc-500 font-medium">
                    {completedGoalsCount} Achieved ({completedPct}%)
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* 5-COLUMN TOTAL MONTHLY COUNTS DISPLAY STRIP */}
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.14 }}
            className="ud-kpi grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-0 mb-8 rounded-[24px] overflow-hidden"
            style={{ border:"1px solid rgba(255,255,255,0.9)", boxShadow:"0 12px 40px rgba(0,0,0,0.05)" }}>
            {metricsSummary.map((s, i) => (
              <motion.div key={s.id} whileHover={{ background:"rgba(255,255,255,0.95)" }}
                className="relative overflow-hidden px-5 py-4 flex items-center gap-3"
                style={{ background: "rgba(255,255,255,0.85)", borderRight: i < 4 ? `1px solid rgba(239,229,216,0.5)` : "none" }}>
                <div className="absolute bottom-0 left-0 right-0 h-[2px]"
                  style={{ background:`linear-gradient(90deg,${s.accent},${s.accent}88)` }} />
                <div className="w-9 h-9 rounded-[12px] flex items-center justify-center flex-shrink-0"
                  style={{ background:`linear-gradient(135deg,${s.accent},${s.accent}CC)`, boxShadow:`0 6px 16px ${s.accent}40` }}>
                  <s.icon size={16} style={{ color:"#fff" }} />
                </div>
                <div className="min-w-0">
                  <p className="ud-kpi-val font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.8px", lineHeight:1 }}>{s.val}</p>
                  <p className="ud-kpi-lbl text-[10px] font-bold tracking-[0.3px] mt-1 text-zinc-500 truncate">{s.title}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* FILTER & TAB BAR */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.16 }}
            className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="ud-scroll flex gap-2 flex-wrap">
              {CATEGORIES.map(cat => (
                <motion.button key={cat} onClick={() => setActiveFilter(cat)} whileTap={{ scale:0.94 }}
                  className="px-4 py-2 rounded-full text-[12px] font-bold border-0 outline-none cursor-pointer"
                  style={activeFilter === cat
                    ? { background:`linear-gradient(135deg,${T.orange},${T.orange2})`, color:"#fff", boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif", transform:"translateY(-1px)" }
                    : { background:"rgba(255,255,255,0.82)", color:T.muted, border:`1px solid ${T.border}`, fontFamily:"'Inter',sans-serif" }
                  }>
                  {cat}
                </motion.button>
              ))}
            </div>
            <div className="ud-scroll flex">
              {["active", "done"].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className="w-36 py-3 font-bold text-[13px] capitalize"
                  style={{ background:"transparent", border:"none", cursor:"pointer",
                    fontFamily:"'Inter',sans-serif",
                    color: activeTab === tab ? T.orange : T.muted,
                    borderBottom: activeTab === tab ? `2.5px solid ${T.orange}` : "2.5px solid transparent" }}>
                  {tab === "active" ? "Current Month" : "Past Months Archive"}
                </button>
              ))}
            </div>
          </motion.div>

          {/* MAIN GOALS GRID & HISTORY */}
          <AnimatePresence mode="wait">
            {activeTab === "active" && (
              <motion.div key="active" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
                {filteredGoals.length === 0 ? (
                  <div className="text-center py-12 mb-8 rounded-[24px]" style={glass}>
                    <p className="text-[15px] font-bold text-[#1F1F1F] mb-1">No Goals in this Category</p>
                    <p className="text-[13px] text-zinc-500">
                      Select a different category filter or view your completed milestones in the <strong>Past Months Archive</strong> tab.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-5 mb-8"
                    style={{ gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))" }}>
                    {filteredGoals.map((g, i) => (
                      <GoalCard
                        key={g.id}
                        goal={g}
                        delay={i * 0.06}
                        currentWeekIndex={currentWeekIndex}
                      />
                    ))}
                  </div>
                )}
                <HistoryChart goals={filteredGoals} currentWeekIndex={currentWeekIndex} />
              </motion.div>
            )}

            {activeTab === "done" && (
              <motion.div key="done" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="font-black text-[22px] text-[#1F1F1F]"
                      style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.5px" }}>Previous Months History & Milestones Archive</h2>
                    <p className="text-[12px] text-zinc-500 mt-0.5">Archieved monthly performance history, targets & badges from past cycles</p>
                  </div>
                  <span className="text-[11px] font-extrabold px-3.5 py-1.5 rounded-full bg-emerald-600/10 text-emerald-800 border border-emerald-600/20 flex items-center gap-1.5">
                    <FiAward size={13} /> Archived Records Persisted
                  </span>
                </div>

                {previousMonthsArchive.length === 0 ? (
                  <div className="text-center py-12 rounded-[24px] mb-8" style={glass}>
                    <FiAward size={36} className="mx-auto mb-3 text-amber-500/60" />
                    <p className="text-[15px] font-bold text-[#1F1F1F] mb-1">No Previous Month History Recorded Yet</p>
                    <p className="text-[13px] text-zinc-500 max-w-md mx-auto">
                      Your active monthly targets and progress for August 2026 will automatically archive here when the next cycle begins on September 1st!
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 mb-8">
                    {previousMonthsArchive.map((g, i) => {
                      const Icon = g.icon;
                      const BadgeIcon = g.pct >= 100 ? FiAward : g.pct >= 75 ? FiStar : FiShield;
                      const badgeTitle = g.pct >= 100 ? "Gold Badge" : g.pct >= 75 ? "Silver Badge" : "Bronze Badge";
                      const badgeBg = g.pct >= 100 ? "#F29A0C" : g.pct >= 75 ? "#059669" : "#B45309";

                      return (
                        <motion.div key={g.id}
                          initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }}
                          transition={{ delay: i*0.07 }} whileHover={{ y:-3 }}
                          className="rounded-[24px] p-5 relative overflow-hidden"
                          style={{ background:g.bg, border:`1px solid ${g.border}`,
                            boxShadow:"0 12px 40px rgba(0,0,0,0.05)" }}>
                          
                          <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div className="flex items-center gap-4 min-w-0">
                              <div className="w-12 h-12 rounded-[16px] flex items-center justify-center flex-shrink-0 text-white"
                                style={{ background:`linear-gradient(135deg,${g.accent},${g.accent}CC)`, boxShadow:`0 6px 16px ${g.accent}40` }}>
                                <Icon size={22} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>{g.title}</p>
                                  <span className="text-[10px] font-extrabold px-2.5 py-0.5 rounded-full bg-black/5 text-zinc-600">
                                    {g.monthName}
                                  </span>
                                </div>
                                <p className="text-[12px] mt-1 text-zinc-600 font-medium">
                                  Final Achieved: <strong>{g.current.toLocaleString()}</strong> / {g.target.toLocaleString()} {g.unit} ({g.pct}% Completed)
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-[10px] font-extrabold px-3 py-1.5 rounded-full text-white shadow-sm flex items-center gap-1.5"
                                style={{ background: badgeBg }}>
                                <BadgeIcon size={12} /> {badgeTitle}
                              </span>
                              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-extrabold"
                                style={{ background:"rgba(107,138,90,0.14)", color:"#27500A" }}>
                                <FiAward size={13} /> +{g.pointsReward} pts
                              </div>
                            </div>
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-4">
                            <ProgressBar pct={g.pct} accent={g.accent} accent2={g.accent2} />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </UserDashboardLayout>
  );
}